import React, { useEffect, useState } from 'react';
import dashboardService from '../../services/dashboardService';
import LoadingSpinner from '../common/LoadingSpinner';
import Alert from '../common/Alert';
import './OperationalDashboard.css';

const NO_DATA = '—';

const formatPercent = (value) => (value === null || value === undefined ? NO_DATA : `${value}%`);
const formatHours = (value) => (value === null || value === undefined ? NO_DATA : value);

const FUNNEL_STEPS = [
  { key: 'invited', label: 'Invited' },
  { key: 'accepted', label: 'Accepted' },
  { key: 'consented', label: 'Consented' },
  { key: 'submitted', label: 'Submitted' },
  { key: 'reported', label: 'Report ready' }
];

const OUTCOMES = [
  { key: 'approve', label: 'Approved', className: 'green', color: 'var(--chart-approve)' },
  { key: 'shortlist', label: 'Shortlisted', className: 'blue', color: 'var(--chart-shortlist)' },
  { key: 'request_information', label: 'Info requested', className: 'amber', color: 'var(--chart-info)' },
  { key: 'decline', label: 'Declined', className: 'red', color: 'var(--chart-decline)' }
];

const pieGradient = (distribution, total) => {
  let start = 0;
  const stops = OUTCOMES.filter(({ key }) => distribution[key] > 0).map(({ key, color }) => {
    const end = start + (distribution[key] / total) * 100;
    const stop = `${color} ${start}% ${end}%`;
    start = end;
    return stop;
  });
  return `conic-gradient(${stops.join(', ')})`;
};

const OperationalDashboard = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    dashboardService
      .getAll()
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.response?.data?.message || 'Could not load dashboard metrics.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) return <Alert type="error" message={error} />;
  if (!data) return <LoadingSpinner />;

  const { operational, decisions, funnel } = data;
  const apps = operational.applications;
  const invited = funnel.invited;
  const decisionTotal = decisions.total;

  return (
    <div className="op-dashboard">
      <div className="kpi-row">
        <div className="kpi-card"><h3>Total Apps</h3><p>{apps.total.toLocaleString()}</p></div>
        <div className="kpi-card"><h3>Completion Rate</h3><p>{formatPercent(apps.completion_rate)}</p></div>
        <div className="kpi-card"><h3>Avg Time to Decision (hrs)</h3><p>{formatHours(apps.avg_hours_to_decision)}</p></div>
        <div className="kpi-card"><h3>Manual Review</h3><p>{formatPercent(operational.verifications.manual_review_rate)}</p></div>
      </div>

      <div className="charts-row">
        <div className="chart-card">
          <h3>Funnel Conversion</h3>
          {invited === 0 ? (
            <p className="chart-empty">No invitations sent yet.</p>
          ) : (
            <div className="css-funnel">
              {FUNNEL_STEPS.map(({ key, label }) => {
                const count = funnel[key];
                const pct = Math.round((count / invited) * 100);
                return (
                  <div key={key} className="funnel-step" style={{ width: `${Math.max(pct, 12)}%` }}>
                    {label} ({count} · {pct}%)
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="chart-card">
          <h3>Decision Outcomes</h3>
          {decisionTotal === 0 ? (
            <p className="chart-empty">No decisions recorded yet.</p>
          ) : (
            <>
              <div
                className="css-pie-chart"
                role="img"
                aria-label="Decision outcomes"
                style={{ background: pieGradient(decisions.outcome_distribution, decisionTotal) }}
              ></div>
              <div className="pie-legend">
                {OUTCOMES.map(({ key, label, className }) => {
                  const count = decisions.outcome_distribution[key];
                  return (
                    <span key={key} className="legend-item">
                      <div className={`box ${className}`}></div> {label} ({count})
                    </span>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default OperationalDashboard;
